import type { ChangeEventHandler } from 'react'
import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalBody from 'LuxKit/Modal/ModalBody'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import HeroImage from 'content/components/HeroImage'
import Group from 'components/utils/Group'
import AlertChannelChoice from 'components/Common/Alerts/AlertChannelChoice'
import { usePlaceById } from 'hooks/usePlace'
import useDestinationImageId from 'hooks/useDestinationImageId'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { AlertChannelChoiceValues } from 'hooks/Alerts/useAlertChannelSelection'
import useAlertChannelSelection from 'hooks/Alerts/useAlertChannelSelection'
import { mediaQueryUp } from 'components/utils/breakpoint'

export type { ContentSubscriptions } from 'components/Common/Alerts/alertContentSubscriptions'

/** The delivery choices the watch modal collects, shared with every other alert surface. */
export type DestinationWatchPreferences = AlertChannelChoiceValues

interface Props {
  placeId: string
  onStartWatching: (preferences: DestinationWatchPreferences) => Promise<boolean>
}

const StyledHeroImage = styled(HeroImage)`
&& {
  min-height: unset;
  height: ${rem(200)};

  ${mediaQueryUp.tablet} {
    height: ${rem(280)};
  }
}
`

function WatchDestinationModal(props: Props) {
  const { placeId, onStartWatching } = props
  const intl = useIntl()
  const place = usePlaceById(placeId)
  const placeName = place?.name
  const destinationImage = useDestinationImageId(placeId, placeName)
  const modalElement = useModalElementContext()

  const selection = useAlertChannelSelection()
  const { loaded, channelsLoaded, email, sms, showMarketingOptIn, subscribeToTodaysEscapes, setSubscribeToTodaysEscapes, choice } = selection

  const [saving, setSaving] = useState<boolean>(false)
  // Mapped destination images can be missing from the CDN, fall back to the imageless layout
  // rather than leaving a broken image in the modal.
  const [imageUnavailable, setImageUnavailable] = useState<boolean>(false)

  const handleImageError = useCallback(() => {
    setImageUnavailable(true)
  }, [])

  const handleTodaysEscapesChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    setSubscribeToTodaysEscapes(e.currentTarget.checked)
  }, [setSubscribeToTodaysEscapes])

  const handleStartWatching = useCallback(async() => {
    setSaving(true)
    const created = await onStartWatching(choice)
    setSaving(false)
    if (created) modalElement.resolve()
  }, [onStartWatching, choice, modalElement])

  const heading = placeName ?
      intl.formatMessage({
        id: 'eK8Ao16JqA-fbSvVHqxy',
        defaultMessage: 'Never miss a new offer in {placeName}',
        description: 'Modal heading inviting the user to receive alerts for new offers in the destination they searched',
      }, { placeName }) :
    undefined

  const description = placeName ?
      intl.formatMessage({
        id: 'dHW6GnzJqUfILnuoO7Cx',
        defaultMessage: 'We’ll notify you as soon as new offers in {placeName} become available, so you can book before they sell out.',
        description: 'Modal body explaining that the user is alerted as soon as new offers for the watched destination are published',
      }, { placeName }) :
    undefined

  const todaysEscapesLabel = intl.formatMessage({
    id: '_UsNX54Mn_dbjMOmlB9k',
    defaultMessage: 'I’d also like to receive marketing communications from Luxury Escapes, including offers, travel inspiration and trip updates. Opt out anytime.',
    description: 'Checkbox label offering an unsubscribed user marketing communications while creating a destination offer alert',
  })

  const startWatchingText = intl.formatMessage({
    id: '_5uyvr7oWFP6Jjx4v321',
    defaultMessage: 'Start watching',
    description: 'Primary action label that confirms the user wants alerts for new offers in this destination',
  })

  const channelsUnavailableText = intl.formatMessage({
    id: 'AGmnkVl276i7njSx2mFZ',
    defaultMessage: 'We couldn’t load your notification settings. Please try again later.',
    description: 'Message shown in the destination watch modal when the saved email and SMS alert channels could not be loaded, so the watch cannot be created',
  })

  // Both channels off delivers nothing, and the copy above promises a notification.
  const hasDeliveryChannel = email || sms
  const canStartWatching = !saving && loaded && channelsLoaded && hasDeliveryChannel

  const hasImage = !!destinationImage && !imageUnavailable

  return (
    <ModalBase>
      {hasImage && <>
        <ModalHeader title="" isOverlay />
        <StyledHeroImage id={destinationImage} onError={handleImageError} />
      </>}
      {!hasImage && <ModalHeader title={heading ?? ''} subtitle={description} />}
      <ModalContent>
        <ModalBody>
          <Group direction="vertical" gap={20}>
            {hasImage && <Group direction="vertical" gap={12}>
              {heading && <Heading variant="heading3">{heading}</Heading>}
              {description && <BodyText variant="medium">{description}</BodyText>}
            </Group>}
            <AlertChannelChoice selection={selection} idPrefix="destination-watch-channel" />
            <TextButton kind="primary" fit="full-width" disabled={!canStartWatching} onClick={handleStartWatching}>{startWatchingText}</TextButton>
            {loaded && !channelsLoaded && <BodyText variant="small" colour="critical">{channelsUnavailableText}</BodyText>}
            {showMarketingOptIn && <CheckboxInput checked={subscribeToTodaysEscapes} onChange={handleTodaysEscapesChange}>
              {todaysEscapesLabel}
            </CheckboxInput>}
          </Group>
        </ModalBody>
      </ModalContent>
    </ModalBase>
  )
}

export default WatchDestinationModal
